/**
 * The client-prefill queue message: the one contract between the façade, which PLANS a sponsored
 * workspace's prefill at setup, and `services/client-prefill`, which WRITES it
 * (crossmarc/steward-app#1491, SCS 4.3; design memo §5-§6; coordinator decision "option A",
 * 2026-09-16).
 *
 * ## Why the plan rides in the message, not the intake id alone
 *
 * The writer runs as `system:client-prefill`, whose appcore scope (appcore#689) is six portfolio
 * types, `create` and `read` only. It cannot read `advisor-intake`, so it cannot re-derive a plan
 * from the intake. The façade already holds the intake at setup (read under the advisor's own
 * token, membership-authorized), so it runs {@link planPrefill} there and enqueues the frozen
 * result. A redelivery therefore replays exactly the rows planned at setup and never re-derives
 * from anything.
 *
 * ## What the message carries, and what it must not
 *
 * Ids, and the rows `planPrefill` produced. The rows ARE the family's figures (balances, names,
 * institutions), so the queue is SSE-encrypted and no consumer may log a row's attributes. Nothing
 * else from the intake (no email, no verification hashes, no firm-side answers) is in it.
 *
 * ## The size bound
 *
 * SQS's hard limit is far above a typical plan, but an intake can be large (`MAX_INTAKE_ROWS` per
 * array, free-text fields up to their schema caps), so the façade measures the SERIALIZED body and
 * refuses to enqueue past {@link CLIENT_PREFILL_MESSAGE_MAX_BYTES}, recording `too-large` on the
 * intake link instead. Measured in UTF-8 bytes, not string length, because SQS counts bytes.
 */
import { z } from 'zod';

/**
 * The six entity types a prefill may write: the same six as appcore's `CLIENT_PREFILL_SCOPE`
 * (crossmarc/appcore `packages/core/src/auth/system-plane.ts`, appcore#689, read at `aef54ab`) and as
 * the `execute-api` type segments `deploy/lib/intake-stack.ts` grants the prefill role. Three copies,
 * because neither appcore nor `deploy/` can import this package: `clientPrefillMessage.test.ts`
 * pins this list and `deploy/test/client-prefill-stack.test.ts` pins the grant's exact ARNs; nothing
 * pins either against appcore.
 */
export const CLIENT_PREFILL_TYPE_CODES = ['account', 'asset', 'valuation', 'liability', 'contact', 'person'] as const;
export type ClientPrefillTypeCode = (typeof CLIENT_PREFILL_TYPE_CODES)[number];

/** The message schema version. A consumer refuses any other. */
export const CLIENT_PREFILL_MESSAGE_VERSION = 1;

/**
 * The largest serialized body the façade will enqueue, in UTF-8 bytes. Comfortably under SQS's
 * limit, so an envelope or attribute added later cannot push a body the façade accepted past what
 * SQS accepts.
 */
export const CLIENT_PREFILL_MESSAGE_MAX_BYTES = 200_000;

/**
 * The most rows one message may carry. Well above the largest plan `planPrefill` can produce
 * (each of its arrays is bounded by `MAX_INTAKE_ROWS`, a snapshot by its own account cap), and a
 * bound the consumer can check before doing any work.
 */
export const CLIENT_PREFILL_MAX_ROWS = 1_000;

/**
 * An appcore workspace or tenant id as it goes into a URL path. Letters, digits, `_` and `-`
 * only: no `/`, and no `.`, so neither a separator nor a `..` segment can reach the
 * `/system/w/{workspaceId}/…` path the consumer builds from it.
 */
const PathSafeId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

/**
 * A row id `planPrefill` can emit: `pf` + 30 lowercase hex for every row, and the valuation
 * form `valuationInstanceId(assetId, date)` = `<asset id>_YYYY-MM-DD`. Nothing else.
 */
export const CLIENT_PREFILL_ROW_ID_PATTERN = /^pf[0-9a-f]{30}(?:_\d{4}-\d{2}-\d{2})?$/;

export const ClientPrefillRowSchema = z.strictObject({
  typeCode: z.enum(CLIENT_PREFILL_TYPE_CODES),
  instanceId: z.string().regex(CLIENT_PREFILL_ROW_ID_PATTERN),
  attributes: z.record(z.string(), z.unknown()),
});
export type ClientPrefillRow = z.infer<typeof ClientPrefillRowSchema>;

export const ClientPrefillMessageSchema = z.strictObject({
  v: z.literal(CLIENT_PREFILL_MESSAGE_VERSION),
  /** The `advisor-intake` the plan came from. For logs and correlation only; never read. */
  intakeId: PathSafeId,
  /**
   * The setup the plan belongs to, and the seed of every row id. The client workspace id: a
   * setup is keyed by (firm tenant, client workspace) at appcore, and a cancelled setup that is
   * set up again gets a NEW workspace, so its rows can never collide with the old ones.
   */
  setupId: PathSafeId,
  firmTenantId: PathSafeId,
  clientTenantId: PathSafeId,
  /** The ONLY workspace the consumer writes to. */
  clientWorkspaceId: PathSafeId,
  rows: z.array(ClientPrefillRowSchema).max(CLIENT_PREFILL_MAX_ROWS),
});
export type ClientPrefillMessage = z.infer<typeof ClientPrefillMessageSchema>;

/** Rows per type, as recorded on the intake link. Every type present, zero when none. */
export type ClientPrefillCounts = Record<ClientPrefillTypeCode, number>;

export const ClientPrefillCountsSchema = z.strictObject({
  account: z.number().int().nonnegative(),
  asset: z.number().int().nonnegative(),
  valuation: z.number().int().nonnegative(),
  liability: z.number().int().nonnegative(),
  contact: z.number().int().nonnegative(),
  person: z.number().int().nonnegative(),
});

/** Counts a plan's rows by type. A row of any other type throws: the plan is not one this module knows. */
export function countClientPrefillRows(rows: readonly { typeCode: string }[]): ClientPrefillCounts {
  const counts: ClientPrefillCounts = { account: 0, asset: 0, valuation: 0, liability: 0, contact: 0, person: 0 };
  for (const row of rows) {
    if (!(CLIENT_PREFILL_TYPE_CODES as readonly string[]).includes(row.typeCode)) {
      throw new Error(`client prefill: a planned row has type "${row.typeCode}", which prefill never writes`);
    }
    counts[row.typeCode as ClientPrefillTypeCode] += 1;
  }
  return counts;
}

/**
 * Validates and serializes a message, measuring the body the way SQS does.
 *
 * `too-large` carries the byte count only, never the body. A message that does not parse throws:
 * that is a planner or caller defect, not a state to record.
 */
export function serializeClientPrefillMessage(
  message: ClientPrefillMessage,
): { kind: 'ok'; body: string; bytes: number } | { kind: 'too-large'; bytes: number } {
  const parsed = ClientPrefillMessageSchema.parse(message);
  const body = JSON.stringify(parsed);
  const bytes = new TextEncoder().encode(body).byteLength;
  return bytes > CLIENT_PREFILL_MESSAGE_MAX_BYTES ? { kind: 'too-large', bytes } : { kind: 'ok', body, bytes };
}
